import React, { useEffect, useRef, useState } from 'react';
import { toast } from '../../../ui/toast';
import { Modal } from '../../../ui/modal/modal';
import {
  useCreateCCAQuestionMutation,
  useGetStorageUrlQuery,
  usePresignCCAImageUploadMutation,
  useUpdateCCAQuestionMutation,
} from '../../../utils/api_service/ccaApi';
import {
  CCAQuestion,
  CreateCCAQuestionRequest,
} from '../../../utils/types/cca_types';
import { CCA_LEVEL_LABELS, CCA_SUBDOMAINS } from './ccaConstants';

// ─── Types ──────────────────────────────────────────────────────────────────

interface OptionDraft {
  text: string;
  isCorrect: boolean;
  imageKey?: string;
  imagePreview?: string;
}

interface FormState {
  subdomain: string;
  level: number;
  questionType: 'multiple-choice' | 'short-answer';
  questionText: string;
  questionImageKey?: string;
  questionImagePreview?: string;
  options: OptionDraft[];
  idealAnswer: string;
}

const INITIAL_FORM: FormState = {
  subdomain: CCA_SUBDOMAINS[0],
  level: 1,
  questionType: 'multiple-choice',
  questionText: '',
  options: [
    { text: '', isCorrect: false },
    { text: '', isCorrect: false },
  ],
  idealAnswer: '',
};

// ─── Helpers ────────────────────────────────────────────────────────────────

/** Resolves a stored imageKey to a display URL using the optional env var base. */
const getCCAImageUrl = (imageKey: string | undefined): string | undefined => {
  if (!imageKey) return undefined;
  const base = process.env.REACT_APP_CCA_IMAGE_BASE_URL;
  if (!base) return undefined;
  return `${base.replace(/\/$/, '')}/${imageKey}`;
};

// ─── SignedImageDisplay Component ────────────────────────────────────────────

interface SignedImageDisplayProps {
  imageKey: string;
  alt?: string;
  className?: string;
  fallbackText?: string;
}

const SignedImageDisplay: React.FC<SignedImageDisplayProps> = ({
  imageKey,
  alt = 'Image',
  className = 'h-14 w-14 rounded-lg object-cover ring-1 ring-slate-200',
  fallbackText,
}) => {
  const { data, isLoading, isError } = useGetStorageUrlQuery(imageKey);

  if (isLoading) {
    return (
      <div className='h-14 w-14 rounded-lg bg-slate-100 animate-pulse ring-1 ring-slate-200' />
    );
  }

  if (isError || !data?.url) {
    return (
      <div className='flex h-14 items-center gap-1.5 rounded-lg bg-slate-100 px-2.5 py-1 ring-1 ring-slate-200 text-[10px] text-slate-500'>
        <svg
            viewBox='0 0 16 16'
            className='h-3.5 w-3.5 shrink-0'
            fill='none'
            stroke='currentColor'
            strokeWidth={1.5}
        >
          <rect x='2' y='2' width='12' height='12' rx='2' />
          <circle cx='5.5' cy='5.5' r='1.5' />
          <polyline points='2,11 5,8 8,10 11,7 14,10' />
        </svg>
        <span className='max-w-[80px] truncate'>
          { fallbackText || imageKey.split('/').pop() }
        </span>
      </div>
    );
  }

  return <img src={data.url} alt={alt} className={className} />;
};

// ─── Validation ─────────────────────────────────────────────────────────────

function validate(form: FormState): string | null {
  if (!form.questionText.trim()) return 'Question text is required.';
  if (form.questionText.trim().length < 10)
    return 'Question text must be at least 10 characters.';
  if (!form.idealAnswer.trim()) return 'Ideal answer is required.';
  if (form.questionType === 'multiple-choice') {
    const filled = form.options.filter((o) => o.text.trim());
    if (filled.length < 2) return 'At least 2 options are required.';
    if (!filled.some((o) => o.isCorrect))
      return 'At least one option must be marked as correct.';
  }
  return null;
}

// ─── ImageUploadField ────────────────────────────────────────────────────────

interface ImageUploadFieldProps {
  imageKey?: string;
  imagePreview?: string;
  isUploading: boolean;
  onUpload: (file: File) => void;
  onClear: () => void;
  label?: string;
}

const ImageUploadField: React.FC<ImageUploadFieldProps> = ({
  imageKey,
  imagePreview,
  isUploading,
  onUpload,
  onClear,
  label = '+ Add image',
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const hasImage = !!(imageKey || imagePreview);

  return (
    <div className='flex items-center gap-2 mt-1.5'>
      { imagePreview ? (
        <div className='relative shrink-0'>
          <img
              src={imagePreview}
              alt='Preview'
              className='h-14 w-14 rounded-lg object-cover ring-1 ring-slate-200'
          />
          <button
              type='button'
              onClick={onClear}
              className='absolute -right-1.5 -top-1.5 flex h-4 w-4 items-center justify-center rounded-full bg-rose-500 text-[10px] text-white shadow hover:bg-rose-600'
          >
            ×
          </button>
        </div>
      ) : imageKey ? (
        <div className='relative shrink-0'>
          <SignedImageDisplay
              imageKey={imageKey}
              className='h-14 w-14 rounded-lg object-cover ring-1 ring-slate-200'
          />
          <button
              type='button'
              onClick={onClear}
              className='absolute -right-1.5 -top-1.5 flex h-4 w-4 items-center justify-center rounded-full bg-rose-500 text-[10px] text-white shadow hover:bg-rose-600'
          >
            ×
          </button>
        </div>
      ) : null }

      <label
          className={`flex cursor-pointer items-center gap-1.5 rounded-lg border border-dashed px-2.5 py-1.5 text-xs font-medium transition-colors ${
            isUploading
              ? 'border-slate-200 text-slate-400 cursor-not-allowed'
              : 'border-slate-300 text-slate-500 hover:border-emerald-400 hover:text-emerald-600'
          }`}
      >
        { isUploading ? (
          <>
            <svg
                className='h-3 w-3 animate-spin'
                viewBox='0 0 16 16'
                fill='none'
                stroke='currentColor'
                strokeWidth={2}
            >
              <circle
                  cx='8'
                  cy='8'
                  r='6'
                  strokeDasharray='24'
                  strokeDashoffset='8'
              />
            </svg>
            Uploading…
          </>
        ) : hasImage ? (
          'Change image'
        ) : (
          label
        ) }
        <input
            ref={inputRef}
            type='file'
            accept='image/*'
            className='hidden'
            disabled={isUploading}
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) {
                onUpload(file);
                e.target.value = '';
              }
            }}
        />
      </label>
    </div>
  );
};

// ─── Component ──────────────────────────────────────────────────────────────

interface Props {
  isOpen: boolean;
  onClose: () => void;
  question?: CCAQuestion;
}

const toFormSubdomain = (snake: string): string =>
  snake
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');

const toFormType = (apiType: string): 'multiple-choice' | 'short-answer' =>
  apiType === 'multiple_choice' ? 'multiple-choice' : 'short-answer';

export const CreateQuestionDialog: React.FC<Props> = ({
  isOpen,
  onClose,
  question,
}) => {
  const isEditMode = !!question;

  const [form, setForm] = useState<FormState>(INITIAL_FORM);
  const [errors, setErrors] = useState<string | null>(null);
  const [uploadingTarget, setUploadingTarget] =
    useState<'question' | number | null>(null);

  const [createQuestion, { isLoading: isCreating }] =
    useCreateCCAQuestionMutation();
  const [updateQuestion, { isLoading: isUpdating }] =
    useUpdateCCAQuestionMutation();
  const [presign] = usePresignCCAImageUploadMutation();
  const isLoading = isCreating || isUpdating;

  useEffect(() => {
    if (isOpen && question) {
      setForm({
        subdomain: toFormSubdomain(question.subdomain),
        level: question.level,
        questionType: toFormType(question.questionType),
        questionText: question.question.text,
        questionImageKey: question.question.imageKey,
        questionImagePreview: undefined,
        options:
          question.options.length > 0
            ? question.options.map((o) => ({
              text: o.text,
              isCorrect: o.isCorrect,
              imageKey: o.imageKey,
              imagePreview: undefined,
            }))
            : [
              { text: '', isCorrect: false },
              { text: '', isCorrect: false },
            ],
        idealAnswer: question.idealAnswer ?? '',
      });
    } else if (isOpen && !question) {
      setForm(INITIAL_FORM);
    }
    setErrors(null);
  }, [isOpen, question]);

  const setField = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setErrors(null);
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  // ─── Image upload ────────────────────────────────────────────────────────

  const handleUploadImage = async (file: File, target: 'question' | number) => {
    setUploadingTarget(target);
    try {
      const response = await presign(file.name).unwrap();
      const uploadUrl: string = response.url;
      const imageKey: string = response.imageKey;

      const putResponse = await fetch(uploadUrl, {
        method: 'PUT',
        body: file,
      });
      if (!putResponse.ok) {
        throw new Error(`S3 upload rejected: ${putResponse.status}`);
      }

      const preview = URL.createObjectURL(file);

      if (target === 'question') {
        setForm((prev) => ({
          ...prev,
          questionImageKey: imageKey,
          questionImagePreview: preview,
        }));
      } else {
        setForm((prev) => ({
          ...prev,
          options: prev.options.map((o, i) =>
            i === target ? { ...o, imageKey, imagePreview: preview } : o,
          ),
        }));
      }
    } catch {
      toast.error('Image upload failed.', { position: 'bottom-center' });
    } finally {
      setUploadingTarget(null);
    }
  };

  const clearQuestionImage = () =>
    setForm((prev) => ({
      ...prev,
      questionImageKey: undefined,
      questionImagePreview: undefined,
    }));

  const clearOptionImage = (index: number) =>
    setForm((prev) => ({
      ...prev,
      options: prev.options.map((o, i) =>
        i === index
          ? { ...o, imageKey: undefined, imagePreview: undefined }
          : o,
      ),
    }));

  const handleOptionChange = (
    index: number,
    field: keyof OptionDraft,
    value: string | boolean,
  ) => {
    setErrors(null);
    setForm((prev) => {
      const options = prev.options.map((o, i) =>
        i === index ? { ...o, [field]: value } : o,
      );
      return { ...prev, options };
    });
  };

  const handleMarkCorrect = (index: number) => {
    setErrors(null);
    setForm((prev) => ({
      ...prev,
      options: prev.options.map((o, i) => ({ ...o, isCorrect: i === index })),
    }));
  };

  const addOption = () => {
    if (form.options.length >= 6) return;
    setForm((prev) => ({
      ...prev,
      options: [...prev.options, { text: '', isCorrect: false }],
    }));
  };

  const removeOption = (index: number) => {
    if (form.options.length <= 2) return;
    setForm((prev) => ({
      ...prev,
      options: prev.options.filter((_, i) => i !== index),
    }));
  };

  const handleQuestionTypeChange = (
    type: 'multiple-choice' | 'short-answer',
  ) => {
    setErrors(null);
    setForm((prev) => ({
      ...prev,
      questionType: type,
      options:
        type === 'multiple-choice'
          ? [
            { text: '', isCorrect: false },
            { text: '', isCorrect: false },
          ]
          : [],
    }));
  };

  const handleClose = () => {
    setForm(INITIAL_FORM);
    setErrors(null);
    onClose();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const error = validate(form);
    if (error) {
      setErrors(error);
      return;
    }

    const payload: CreateCCAQuestionRequest = {
      subdomain: form.subdomain.toLowerCase().replace(/ /g, '_'),
      level: form.level,
      questionType: form.questionType.replace(/-/g, '_'),
      question: {
        text: form.questionText.trim(),
        ...(form.questionImageKey && { imageKey: form.questionImageKey }),
      },
      options:
        form.questionType === 'multiple-choice'
          ? form.options
            .filter((o) => o.text.trim())
            .map((o) => ({
              id: crypto.randomUUID(),
              text: o.text.trim(),
              isCorrect: o.isCorrect,
              ...(o.imageKey && { imageKey: o.imageKey }),
            }))
          : [],
      idealAnswer: form.idealAnswer.trim(),
    };

    try {
      if (isEditMode && question) {
        await updateQuestion({
          questionId: question.id,
          body: payload,
        }).unwrap();
        toast.success('Question updated successfully.', {
          position: 'bottom-center',
        });
      } else {
        await createQuestion(payload).unwrap();
        toast.success('Question created successfully.', {
          position: 'bottom-center',
        });
      }
      handleClose();
    } catch (err: unknown) {
      const serverMessage =
        (err as { data?: { error?: string } })?.data?.error ??
        (isEditMode
          ? 'Failed to update question. Please try again.'
          : 'Failed to create question. Please try again.');
      toast.error(serverMessage, { position: 'bottom-center' });
    }
  };

  const inputClass =
    'w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-800 shadow-sm placeholder:text-slate-400 focus:border-emerald-400 focus:outline-none focus:ring-1 focus:ring-emerald-300';
  const selectClass =
    'w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-800 shadow-sm focus:border-emerald-400 focus:outline-none focus:ring-1 focus:ring-emerald-300';
  const labelClass =
    'mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500';

  return (
    <Modal
        visible={isOpen}
        toggle={handleClose}
        toggleable={!isLoading}
        headerText={isEditMode ? 'Edit CCA Question' : 'Create CCA Question'}
        customClass='w-full max-w-2xl'
        modalContent={
          <form onSubmit={handleSubmit} className='px-5 pb-5 pt-2'>
            { /* Row 1: Subdomain + Level + Type */ }
            <div className='mb-4 grid grid-cols-3 gap-3'>
              <div>
                <label className={labelClass}>Subdomain</label>
                <select
                    value={form.subdomain}
                    onChange={(e) => setField('subdomain', e.target.value)}
                    className={selectClass}
                >
                  { CCA_SUBDOMAINS.map((sd) => (
                    <option key={sd} value={sd}>
                      { sd }
                    </option>
                  )) }
                </select>
              </div>

              <div>
                <label className={labelClass}>Level</label>
                <select
                    value={form.level}
                    onChange={(e) => setField('level', Number(e.target.value))}
                    className={selectClass}
                >
                  { Array.from({ length: 10 }, (_, i) => i + 1).map((l) => (
                    <option key={l} value={l}>
                      { l } — { CCA_LEVEL_LABELS[l] }
                    </option>
                  )) }
                </select>
              </div>

              <div>
                <label className={labelClass}>Type</label>
                <select
                    value={form.questionType}
                    onChange={(e) =>
                      handleQuestionTypeChange(
                    e.target.value as 'multiple-choice' | 'short-answer',
                      )
                    }
                    className={selectClass}
                >
                  <option value='multiple-choice'>Multiple Choice</option>
                  <option value='short-answer'>Short Answer</option>
                </select>
              </div>
            </div>

            { /* Question Text + optional image */ }
            <div className='mb-4'>
              <label className={labelClass}>Question Text</label>
              <textarea
                  rows={3}
                  value={form.questionText}
                  onChange={(e) => setField('questionText', e.target.value)}
                  placeholder='Enter the question...'
                  className={`${inputClass} resize-none`}
              />
              <ImageUploadField
                  imageKey={form.questionImageKey}
                  imagePreview={form.questionImagePreview}
                  isUploading={uploadingTarget === 'question'}
                  onUpload={(file) => handleUploadImage(file, 'question')}
                  onClear={clearQuestionImage}
                  label='+ Add question image'
              />
            </div>

            { /* Multiple Choice Options */ }
            { form.questionType === 'multiple-choice' && (
              <div className='mb-4'>
                <label className={labelClass}>
                  Answer Options
                  <span className='ml-1 font-normal normal-case tracking-normal text-slate-400'>
                    (click circle to mark correct)
                  </span>
                </label>
                <div className='space-y-2'>
                  { form.options.map((opt, i) => (
                    <div
                        key={i}
                        className='rounded-lg border border-slate-100 bg-slate-50/60 p-2'
                    >
                      <div className='flex items-center gap-2'>
                        { /* Correct toggle */ }
                        <button
                            type='button'
                            onClick={() => handleMarkCorrect(i)}
                            title='Mark as correct answer'
                            className={`shrink-0 h-5 w-5 rounded-full border-2 transition-colors ${
                              opt.isCorrect
                                ? 'border-emerald-500 bg-emerald-500'
                                : 'border-slate-300 bg-white hover:border-emerald-400'
                            }`}
                        >
                          { opt.isCorrect && (
                            <svg
                                viewBox='0 0 10 10'
                                className='h-full w-full p-0.5 text-white'
                                fill='none'
                                stroke='currentColor'
                                strokeWidth={2}
                            >
                              <polyline points='2,5 4,8 8,2' />
                            </svg>
                          ) }
                        </button>

                        <input
                            type='text'
                            value={opt.text}
                            onChange={(e) =>
                              handleOptionChange(i, 'text', e.target.value)
                            }
                            placeholder={`Option ${i + 1}`}
                            className={inputClass}
                        />

                        { form.options.length > 2 && (
                          <button
                              type='button'
                              onClick={() => removeOption(i)}
                              className='shrink-0 rounded-lg p-1 text-slate-400 hover:bg-rose-50 hover:text-rose-500 transition-colors'
                              title='Remove option'
                          >
                            <svg
                                viewBox='0 0 16 16'
                                className='h-4 w-4'
                                fill='none'
                                stroke='currentColor'
                                strokeWidth={2}
                            >
                              <line x1='4' y1='4' x2='12' y2='12' />
                              <line x1='12' y1='4' x2='4' y2='12' />
                            </svg>
                          </button>
                        ) }
                      </div>
                      <ImageUploadField
                          imageKey={opt.imageKey}
                          imagePreview={opt.imagePreview}
                          isUploading={uploadingTarget === i}
                          onUpload={(file) => handleUploadImage(file, i)}
                          onClear={() => clearOptionImage(i)}
                          label='+ Add option image'
                      />
                    </div>
                  )) }
                </div>
                { form.options.length < 6 && (
                  <button
                      type='button'
                      onClick={addOption}
                      className='mt-2 text-xs font-semibold text-emerald-600 hover:text-emerald-700'
                  >
                    + Add option
                  </button>
                ) }
              </div>
            ) }

            { /* Ideal Answer */ }
            <div className='mb-5'>
              <label className={labelClass}>Ideal Answer</label>
              <input
                  type='text'
                  value={form.idealAnswer}
                  onChange={(e) => setField('idealAnswer', e.target.value)}
                  placeholder={
                    form.questionType === 'multiple-choice'
                      ? 'Expected answer or explanation...'
                      : 'Model answer...'
                  }
                  className={inputClass}
              />
            </div>

            { /* Validation error */ }
            { errors && (
              <p className='mb-4 rounded-lg bg-rose-50 px-3 py-2 text-xs font-medium text-rose-600'>
                { errors }
              </p>
            ) }

            { /* Actions */ }
            <div className='flex justify-end gap-2'>
              <button
                  type='button'
                  onClick={handleClose}
                  disabled={isLoading}
                  className='rounded-lg border border-slate-200 px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-50 disabled:opacity-50 transition-colors'
              >
                Cancel
              </button>
              <button
                  type='submit'
                  disabled={isLoading || uploadingTarget !== null}
                  className='rounded-lg bg-emerald-600 px-5 py-2 text-sm font-semibold text-white shadow-sm hover:bg-emerald-700 disabled:opacity-60 transition-colors'
              >
                { isLoading
                  ? isEditMode
                    ? 'Saving...'
                    : 'Creating...'
                  : isEditMode
                    ? 'Save Changes'
                    : 'Create Question' }
              </button>
            </div>
          </form>
        }
    />
  );
};
